import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Link } from 'react-router-dom';
import { useMailer } from './hooks/useMailer';
import { Helmet } from 'react-helmet';
import './style.css';

function PartnerWithUsSpanish() {
  const { send, loading, submitted, ErrorBox } = useMailer('contact');

  const handleSubmit = (e) => {
    e.preventDefault();
    send(Object.fromEntries(new FormData(e.target).entries()));
  };

  const benefits = [
    { icon: '🌎', title: 'International Experience', desc: 'We welcome students from different countries and cultures, with 20+ years hosting international groups.' },
    { icon: '🏫', title: 'Specialized Programs', desc: 'Courses fully adapted to groups, agencies, and language schools — each program tailored to your needs.' },
    { icon: '🎉', title: 'Cultural Activities', desc: 'Students live authentic experiences in Tlaquepaque, Jalisco — the heart of Mexican culture.' },
    { icon: '🤝', title: 'Full Flexibility', desc: 'We design tailored packages for any duration, group size, or academic requirement.' },
    { icon: '📋', title: 'Official Certificates', desc: 'Participants receive official Culturlingua certificates recognized by educational institutions.' },
    { icon: '✈️', title: 'Logistics Support', desc: 'We help with accommodation, airport pickup, and local transportation coordination.' },
  ];

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Partner With Us | Culturlingua — Spanish School in Tlaquepaque</title>
        <meta name="description" content="Partner with Culturlingua Jalisco and offer personalized Spanish course packages to your students." />
      </Helmet>

      <div className="page-hero">
        <div className="section-tag">For Agencies &amp; Schools</div>
        <h1>Partner With Us</h1>
        <p>Offer personalized Spanish course packages to your students in beautiful Tlaquepaque, Jalisco.</p>
        <div style={{marginTop:'32px', display:'flex', gap:'16px', justifyContent:'center', flexWrap:'wrap'}}>
          <a href="#partner-form" className="btn-glow">Get in Touch →</a>
          <Link to="/OurLocation" className="btn-outline">Our Location</Link>
        </div>
      </div>

      <section style={{background:'var(--navy)', padding:0}}>
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          navigation pagination={{ clickable: true }}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          loop={true} className="modern-swiper" style={{maxHeight:'420px'}}
        >
          {[
            { src: './assets/internationalization.png', caption: 'Internationalization Programs' },
            { src: './assets/tlaquepaque.avif', caption: 'Immerse Your Students in Mexican Culture' },
            { src: './assets/number.webp', caption: 'Tailored Spanish Packages for Every Level' },
          ].map(({ src, caption }) => (
            <SwiperSlide key={caption}>
              <div className="gallery-card" style={{borderRadius:0}}>
                <img src={src} alt={caption} style={{height:'420px', width:'100%', objectFit:'cover', display:'block'}} />
                <div className="caption" style={{fontSize:'1.1rem', padding:'40px 32px 24px'}}>{caption}</div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      <section style={{background:'var(--cream)', padding:'80px 48px'}}>
        <div style={{maxWidth:'1200px', margin:'0 auto'}}>

          <div style={{textAlign:'center', marginBottom:'48px'}}>
            <div className="section-tag">Why Partner with Us</div>
            <h2 className="section-headline">Let's build <em>together</em></h2>
            <p className="section-sub" style={{margin:'0 auto'}}>
              We work with agents, international groups and language schools to offer unique Spanish learning experiences in Mexico.
            </p>
          </div>

          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'20px', marginBottom:'64px'}}>
            {benefits.map((b, i) => (
              <div key={i} style={{background:'white', borderRadius:'16px', padding:'28px', boxShadow:'0 2px 12px rgba(0,0,0,0.05)', transition:'transform 0.25s, box-shadow 0.25s'}}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='0 12px 32px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,0.05)'; }}
              >
                <div style={{fontSize:'2.2rem', marginBottom:'14px'}}>{b.icon}</div>
                <h3 style={{fontFamily:'Fraunces, serif', fontWeight:'700', fontSize:'1.05rem', color:'var(--navy)', marginBottom:'8px'}}>{b.title}</h3>
                <p style={{color:'var(--muted)', fontSize:'0.875rem', lineHeight:'1.6'}}>{b.desc}</p>
              </div>
            ))}
          </div>

          <div style={{background:'var(--navy)', borderRadius:'20px', padding:'48px', textAlign:'center', marginBottom:'64px'}}>
            <p style={{fontFamily:'Fraunces, serif', fontSize:'1.4rem', color:'white', fontWeight:'300', fontStyle:'italic', maxWidth:'600px', margin:'0 auto 32px', lineHeight:'1.7'}}>
              "From short programs to full packages, we design and tailor the best option for your students."
            </p>
            <div style={{display:'flex', justifyContent:'center', gap:'48px', flexWrap:'wrap'}}>
              {[['20+','Years of experience'],['100%','Certified teachers'],['A1–C1','All levels covered']].map(([n, l]) => (
                <div key={l} style={{textAlign:'center'}}>
                  <div style={{fontFamily:'Fraunces, serif', fontSize:'2.2rem', fontWeight:'900', color:'var(--gold)', lineHeight:'1'}}>{n}</div>
                  <div style={{color:'rgba(255,255,255,0.5)', fontSize:'0.8rem', marginTop:'6px'}}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div id="partner-form">
            <div style={{textAlign:'center', marginBottom:'40px'}}>
              <div className="section-tag">Start the Conversation</div>
              <h2 className="section-headline">Interested in <em>partnering?</em></h2>
            </div>
            <div style={{maxWidth:'640px', margin:'0 auto'}}>
              <div style={{background:'white', borderRadius:'20px', padding:'48px', boxShadow:'0 4px 24px rgba(0,0,0,0.07)'}}>
                <form onSubmit={handleSubmit}>
                  <div className="form-row" style={{marginBottom:'16px'}}>
                    <div className="form-field" style={{marginBottom:0}}>
                      <label className="form-label">Name / Organization</label>
                      <input className="form-input" type="text" name="nombre" placeholder="Jane Smith / ABC School" required />
                    </div>
                    <div className="form-field" style={{marginBottom:0}}>
                      <label className="form-label">Phone (optional)</label>
                      <input className="form-input" type="tel" name="telefono" placeholder="+1 555..." />
                    </div>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Email</label>
                    <input className="form-input" type="email" name="correo" placeholder="contact@yourschool.com" required />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Tell us about your interest</label>
                    <textarea className="form-input form-textarea" name="pregunta" placeholder="Group size, duration, time of year, goals..." required style={{minHeight:'120px'}}></textarea>
                  </div>
                  {submitted ? (
                    <div style={{textAlign:'center', padding:'16px 0', color:'var(--blue)', fontWeight:'700'}}>✅ Sent! We'll be in touch soon.</div>
                  ) : (
                    <>
                      {ErrorBox}
                      <button type="submit" className="form-submit" disabled={loading}>
                        {loading ? 'Sending...' : 'Send Partnership Request →'}
                      </button>
                    </>
                  )}
                </form>
              </div>
              <p style={{textAlign:'center', color:'var(--muted)', fontSize:'0.85rem', marginTop:'16px'}}>We respond within 24 business hours.</p>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}

export default PartnerWithUsSpanish;
